/*
 * A wrapper containing balance, available balance, and the ability to pay out when the reseller is project / wallet based.
 * This is intended for use in the reseller dashboard, and in the reseller budget tab
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment'; 
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Link, Tooltip, Typography } from '@mui/material';
import TextWidget from '../ui/TextWidget';
import CurrencyTextField from '../ui/CurrencyTextField';
import CreditCard from '@mui/icons-material/CreditCard';

import AppBindStyles from '../../AppBindStyles';
import PaymentRailsAccountFetch from './PaymentRailsAccountFetch';
import OpenPayoutFetch from './OpenPayoutFetch';
import InitiatePayoutDialog from './InitiatePayoutDialog';
import LoadingSpinner from '../ui/LoadingSpinner';
import { format } from 'date-fns';
import { getCurrencyString } from '../../utils/InputUtils';

const abEnvironment = new AbEnvironment();
const ProjectBalanceAndFloatWidgetsQuery = graphql`
query ProjectBalanceAndFloatWidgetsQuery($accountId: UUID!) {
  getaccountledgersummary(accountId: $accountId, balancedate: null) {
    assetBalance
    walletBalance
    assetAndArBalance
    assetAndFullArBalance
    assetArAuthBalance
    equityBalance
    maxPayoutBalance
    liabilityBalance
    authBalance
  }
  getunreceivedchargedetail(accountid: $accountId) { 
    totalCount nodes { 
      transactionId transactionType transactionTime transDescription extSource extReferral extTransactionId changeType changeAmount 
    } 
  }
  accountTByAccountId(accountId: $accountId) {
    achPercAvailableOnCharge 
    achMinAvailableOnCharge
    ccPercAvailableOnCharge
    ccMinAvailableOnCharge
  }
  allWalletTs(condition: {accountId: $accountId}) {
    totalCount
    nodes {
      walletId
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    width: '100%',
  },
  buttonicon: {
    marginRight: 6,
  },
  instructButton: {
    marginTop: -1,
  },
  widgetContainer: {
    // marginTop: 8,
    //marginBottom: 8,
  }
});
  
/*
 * Props coming in:
 * - narrow           (use narrower tiles)
 * - auth
 * - showEditLink     (show text describing settings, and a link to the budget tab to change the settings)
 * - payFromBalance
 * - autoTopupAmount    (Number)
 * - autoTopupThreshold (Number)
 * - showLimitRequest (show the button / dialog for requesting an increase)
 * - floatLimit       (limit on the account)
 * - accountId        (for lookup)
 * - useBudgets       (what to call the float)
 * - history
 */
class ProjectBalanceAndFloatWidgets extends Component {
    
  constructor(props) {
    super(props);

    this.state = {
      existingPaymentRailsAccount: false,
      showPayoutDialog: false,
      payoutAmount: 0,
      transferAmount: 0,
      executingPayout: false,
      forceFetch: 0,
      existingPayout: false,
      existingPayoutAmount: null,
      existingPayoutDate: null,
    };  
  }

  componentWillUnmount() {
    this.mounted = false; // Antipattern workaround
  }

  componentDidMount() {
    this.mounted = true;

    //see if the account has a payment rails account set up in good standing.
    let accountId = abEnvironment.getUserAttribute('a_id');
    PaymentRailsAccountFetch.fetch(accountId).toPromise()
    .then(praf => {
      
      if (praf.allPaymentSourceTs.totalCount > 0) {
        if (praf.allPaymentSourceTs.nodes[0].psStatusIn === 0) {
          //console.log('We have an exsting PR account in good status');
          if (this.mounted) {
            this.setState({existingPaymentRailsAccount: true})
          }
        }
      }
  
    })
    .catch(err => {
      //nothing to do....
    })

    OpenPayoutFetch.fetch(accountId).toPromise()
    .then(opf => {
      //console.log(JSON.stringify(opf));
      if (opf.lookupopenpayout.payoutOpen === 1) {
        if (this.mounted) {
          this.setState({existingPayout: true, existingPayoutAmount: opf.lookupopenpayout.payoutAmount, existingPayoutDate: opf.lookupopenpayout.requestedDate});
        }
      } else {
        if (this.mounted) {
          this.setState({existingPayout: false});
        }
      }
  
    })
    .catch(err => {
      //nothing to do....
    })

  }

  determinePayoutNextStep = (accountId) => {
    if (this.state.existingPaymentRailsAccount) {
      this.initiatePayout(accountId);
    } else {
      this.goTo('fundingsources', 2)
    }
  }

  showPayoutRequestDialog = () => {
    
    //console.log('showing existing payment rails account');
    this.setState({ showPayoutDialog: true });
  }


  initiatePayout(accountId) {
    const { callPartnerApi } = this.props.auth;

    let forceFetch = this.state.forceFetch;
    forceFetch++;

    let stringAmt = Number(this.state.payoutAmount).toString()

    this.setState({executingPayout: true});

    let aPjwt = abEnvironment.getPjwt();
    let atId  = 'f5c70f86-135a-11e8-bb45-97ef56920c02';
    let today = new Date();

    let payload = {
      operation: "initiatePayout",
      params: {
        pJwt: aPjwt,
        accountId: accountId,
        atId: atId,
        payoutAmount: stringAmt,
        coverFees: true //TODO follow up with Joe.
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      let abpResponse = response.data;
      if (abpResponse == null || abpResponse.status !== 0) {
        let theErrorText = "Unidentified Error";

        if (abpResponse != null && abpResponse.message != null) {
          theErrorText = abpResponse.message;
        }
        this.setState({ executingPayout: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
        return;
      }

      this.setState({ executingPayout: false, existingPayout: true, existingPayoutAmount: this.state.payoutAmount, existingPayoutDate: today, forceFetch: forceFetch, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Payout sent', saveSnackbarDuration: 3000 });
    })
    .then(response => {

      let theBody = abEnvironment.getUserAttribute('a_accountName') + ' has initiated a payout.'
      let payload = {
        operation: "createNotification",
        params: {
          pJwt:             aPjwt,
          accountId:        this.props.accountId,
          notificationType: 'payout',
          severity:         'info',
          subject:          'Payout has been initiated',
          body:             theBody
        }
      }
      
      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
  
    })
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ executingPayout: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
    });
  }


  /**
   * disabled initiate payout in case of no payment account, payout less than $1,
   *   or amount is > maxPayout minus the min balance prior to top-up (if exists.)
   * @param {Number} maxPayoutBalance max payout balance (from the query)
   * @returns True if button disable, false if enabled
   */
  disableInitiatePayout(maxPayoutBalance) {

    if (this.state.existingPaymentRailsAccount === false)
      return true;

    if (this.state.payoutAmount < 1)
      return true;

    // the max for paying out is the minimum between the maxPayoutBalance value and the difference between the full balance and auto-topup threshold.
    let autoTopupAmount = this.props.autoTopupAmount ? this.props.autoTopupAmount : 0;
    let maxPayout = maxPayoutBalance - autoTopupAmount;
    
    if (this.state.payoutAmount > maxPayout)
      return true;
    
    return false;
  }

  goTo(route, theTab) {
    if (theTab != null) {
      localStorage.setItem('psInOut', theTab);
    }
    this.props.history.push(`/${route}`);
  }

  render() {
    const classes = this.props.classes;

    const { isAuthenticatedReseller } = this.props.auth;

    let autoTopupAmountString = getCurrencyString(this.props.autoTopupAmount);
    let autoTopupThresholdString = getCurrencyString(this.props.autoTopupThreshold);

    let existingPayoutAmount = Number(this.state.existingPayoutAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    let existingPayoutDate = format(new Date(this.state.existingPayoutDate), 'yyyy-MM-dd');
      
    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={ProjectBalanceAndFloatWidgetsQuery}
        variables={{accountId: this.props.accountId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {

          if (error) {
            return <div>{error.message}</div>
          } else if (props) {
            // For balance widget.  Originally this was the equity balance, but now that we also have wallet asset, this should
            // be the general account asset balance, plus any general account AR, less any general account liability.
            //let afab = props.getaccountledgersummary.assetAndFullArBalance;
            let afab = Number(props.getaccountledgersummary.equityBalance) - Number(props.getaccountledgersummary.walletBalance);
            let lb = props.getaccountledgersummary.liabilityBalance;
            let abNumber = Number(afab);
            let lbNumber = Number(lb);
            let accountAssetBalance = (abNumber).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            //let accountAssetBalance = (abNumber - lbNumber).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            // For available balance widget
            let ab = props.getaccountledgersummary.assetArAuthBalance;
            // Don't show an available balance greater than the accountAssetBalance.
            if (Number(ab) > (abNumber - lbNumber)) {
              ab = abNumber - lbNumber;
              // If balance is negative, just show 0 for available.
              if (Number(ab) < 0) {
                ab = 0;
              }
            }

            let availableBalance = Number(ab).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            abEnvironment.setUserAttribute("a_availableBalance", availableBalance);

            let payoutAmountString = Number(this.state.payoutAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            let maxPayoutBalance = Number(props.getaccountledgersummary.maxPayoutBalance);
            let mpb = maxPayoutBalance * 1000;
            let maxPayoutBalanceDisplay = maxPayoutBalance.toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            //the max for paying out is the minimum between the maxPayoutBalance value and the difference between the full balance and auto-topup threshold.
            let autoTopupAmount = this.props.autoTopupAmount ? this.props.autoTopupAmount : 0;
            let maxPayoutCheckValue = maxPayoutBalance - autoTopupAmount;

            // There are 3 use cases we are supporting:
            // 1. Reseller looking at own account (on My funding)
            // 2. Customer looking at own account (on My funding)
            // 3. Reseller looking at customer account
            //
            // Depending on which case it is, there will be different verbiage in the various chicklets
            //
            let balanceTitle = "Your balance";
            let balanceTt = "If you markup SaaS apps, bill your services through AppBind, or receive rebates from vendors, you will build an earnings balance in AppBind.";

            //max payout widget text
            let maxPayoutBalanceTitle = "Available balance";
            let maxPayoutBalanceTt = "Your balance may include funds that haven't settled yet, so this indicates what is available to pay out to you, or to transfer to expense wallets.";

            if (abEnvironment.getUserAttribute("a_aType") === "4") {
              // Set the customer messages
              balanceTitle = "Your balance";
              balanceTt = "When you add funds to your account, you'll have a positive balance in AppBind.";
 

            } else if (this.props.accountId !== abEnvironment.getUserAttribute("a_id")) {
              // Set the reseller view customer messages
              balanceTitle = "Balance";
              balanceTt = "When your customer adds funds to their account, they'll have a positive balance in AppBind.";

            }

            if (this.state.executingPayout) {
              return (<LoadingSpinner />);
            } else {
              return (
                <React.Fragment>
                  <Grid container spacing={3} justifyContent="flex-start" className={classes.widgetContainer}>
                    <Grid item>
                      <TextWidget
                        narrow={this.props.narrow}
                        value={accountAssetBalance}
                        description={balanceTitle}
                        tooltip={balanceTt}
                      />
                    </Grid>

                    {/* Max payout currently not intended for customer role view (other roles will show it). */}
                    {(this.props.accountType === 3 || this.props.accountType === '3') &&
                    <Grid item>
                      <TextWidget
                        narrow={this.props.narrow}
                        value={maxPayoutBalanceDisplay}
                        description={maxPayoutBalanceTitle}
                        tooltip={maxPayoutBalanceTt}
                      />
                    </Grid>}

                    {/* Pay out button */}
                    {((maxPayoutBalance <= 0) && isAuthenticatedReseller()) &&
                    <Grid item>
                      <Tooltip title={'You do not have a balance available to pay out.'}>
                        <Button color="primary"  variant="outlined" size="small" disabled={false}>
                          Pay out balance
                        </Button>
                      </Tooltip>
                    </Grid>}
                    {((maxPayoutBalance > 0) && isAuthenticatedReseller() && this.state.existingPayout === true) &&
                    <Grid item>
                      <Tooltip title={"A payout of " + existingPayoutAmount + ' was initiated on ' + existingPayoutDate + ' and is in process.'}>
                        <Button color="primary"  variant="outlined" size="small" disabled={false}>
                          Pay out balance
                        </Button>
                      </Tooltip>
                    </Grid>}
                    {((maxPayoutBalance > 0) && isAuthenticatedReseller() && this.state.existingPayout === false) &&
                    <Grid item>
                      <Button color="primary"  variant="outlined" size="small" 
                              onClick={() => {this.showPayoutRequestDialog()}}>
                        Pay out balance
                      </Button>
                    </Grid>}

                    {this.props.showEditLink &&
                    <Grid item xs={12}>
                      {(this.props.payFromBalance == null || this.props.payFromBalance === 0) &&
                        <Typography variant="body2" style={{ lineHeight: "2" }}>
                          <span>These subscriptions will be automatically charged to your funding sources as set in</span>&nbsp;&nbsp;
                          <Button color="primary" 
                            size="small" 
                            variant="outlined" 
                            className={classes.instructButton}
                            onClick={() => this.goTo('fundingsources', 1)}
                          >
                            <CreditCard className={classes.buttonicon}/> My Funding
                          </Button> 
                        </Typography>
                      }
  
                      {this.props.payFromBalance != null && this.props.payFromBalance !== 0 &&
                        <Typography variant="body2" style={{ lineHeight: "2" }}>
                          <span>Subscriptions will be charged to your available balance of</span>&nbsp;
                          <span style={AppBindStyles.boldLabel}>{availableBalance}</span>
                          {this.props.autoTopupAmount && this.props.autoTopupThreshold &&
                            <React.Fragment>
                              .&nbsp;&nbsp;
                              <span>Your balance will be topped up to</span>&nbsp;
                              <span style={AppBindStyles.boldLabel}>{autoTopupAmountString}</span>&nbsp;
                              <span>when it falls below</span>&nbsp;
                              <span style={AppBindStyles.boldLabel}>{autoTopupThresholdString}</span>
                            </React.Fragment>
                          }

                          &nbsp;<span>as set in</span>&nbsp;&nbsp;
                          <Button color="primary" 
                            size="small" 
                            variant="outlined" 
                            className={classes.instructButton}
                            onClick={() => this.goTo('fundingsources', 1)}
                          >
                            <CreditCard className={classes.buttonicon}/> My Funding
                          </Button>
                        </Typography>
                      }
                    </Grid>
                    }
                  </Grid>
  
                  <InitiatePayoutDialog
                    open={this.state.showPayoutDialog}
                    onClose={() => { this.setState({showPayoutDialog:false}); }}
                    onConfirm={() => { this.determinePayoutNextStep(this.props.accountId) }}
                    onCancel={() => { this.setState({showPayoutDialog:false}); }}
                    onEdit={() => { this.goTo('fundingsources', 2); }}  //note edit button went away and should be removed.
                    amount={this.state.payoutAmount}
                    title={'Pay out balance'}
                    cancelBtn="Cancel"
                    editBtn={false}
                    okBtn={(this.state.existingPaymentRailsAccount === true) ? "Pay out " + payoutAmountString + " now": "Setup payout method"}
                    okDisabled={this.disableInitiatePayout(maxPayoutBalance, ab)}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {this.state.existingPaymentRailsAccount === true ?
                          <React.Fragment>
                            <Typography variant="body1">You have up to {maxPayoutBalanceDisplay} to pay out to the</Typography>
                            <Link style={{marginTop: -2}} component="button" variant="body1" color="primary" onClick={() => {this.goTo('fundingsources', 2);}}>payout method you have on file.</Link>
                          </React.Fragment>
                        : //this.state.existingPaymentRailsAccount === false
                          <Typography variant="body1">{'In order to payout of your balance, you need to first set up a payout method.'}</Typography>
                        }
                      </Grid>

                      <Grid item xs={12}>
                        {this.state.existingPaymentRailsAccount === true &&
                        <CurrencyTextField 
                          id="payout-amount" label="Payout amount"
                          value={this.state.payoutAmount}
                          onChange={ (e) => this.setState({ payoutAmount: e.target.value }) }
                          showErrMsg={true}
                          allowNegative={false}
                          minValue={1}
                          minEnterValue={0}
                          maxValue={maxPayoutCheckValue}
                          maxEnterValue={mpb}
                          maxErrMsg={maxPayoutCheckValue === maxPayoutBalance ? null : `Payout amount can't leave balance below auto-topup amount (${autoTopupAmountString}).`}
                          textAlign="right"
                        />
                        }
                      </Grid>
                    </Grid>
                  </InitiatePayoutDialog>


                </React.Fragment>
              );
            }
          }            
        }}
      />
    );
  }
}

export default withStyles(styleSheet)(ProjectBalanceAndFloatWidgets);